import React, { useContext, useState } from 'react';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonVariant } from '../../../../../components/Button';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../types/translation/Translator';
import { OrderFlowContext } from '../../../../../../context/OrderFlowContext';
import { OrderStatusContext } from '../../../../../../context/OrderStatusContext';
import { useRequest } from '../../../../../../requests/common/requestHook';
import { OrderRequest } from '../../../../../../requests/order.request';
import { OrderFlowAction } from '../../../../../../types/order/orderFlowAction';
import { OrderContext } from '../../../../../../context/OrderContext';
import { GXO } from '../../../../../../types/customer/GXO/GXO';
import GXOCustomFieldModal from './GXOCustomFieldModal';
import { Order, OrderInput } from '../../../../../../types/order';
import { FlowType } from '../../../../../../types/order/orderFlow';

interface UpdateOrderFooterProps {
  order: OrderInput;
  setOrder: (order: OrderInput) => void;
  loading: {
    update: boolean;
  };
  actions: {
    onBack: () => void;
    onAssigned: () => void;
    onAddProduct: () => void;
    onUpdate: () => void;
    onManualCustomField: () => void;
    onIssueReturn: () => void;
  };
}

export default function UpdateOrderFooter({ order, loading, actions, setOrder }: UpdateOrderFooterProps) {
  const navigate = useNavigate();

  const { currentCompany } = useContext(CompanyContext);
  const { orders, setOrders } = useContext(OrderContext);
  const { orderFlows } = useContext(OrderFlowContext);
  const { orderStatuses } = useContext(OrderStatusContext);

  const [open, setOpen] = useState(false);
  const [actionId, setActionId] = useState('');

  const flow = orderFlows.get(order.flowId);
  if (!flow) return null;

  const current = flow.orderActions.find(action => action.statusId === order.statusId);
  if (!current) return null;

  const potentialNextActions = flow.orderActions.filter(action => action.previous.includes(current.statusId));

  const [process, { loading: processLoading }] = useRequest(OrderRequest.process);
  const handleAction = async (action: OrderFlowAction) => {
    if (
      order.companyId === GXO.companyId &&
      ((action.statusId === GXO.arrivedOrderStatusId && order.type === FlowType.inbound) ||
        action.statusId === GXO.documentationReceivedOrderStatusId ||
        action.statusId === GXO.confirmCollectionDateOrderStatusId)
    ) {
      setActionId(action.statusId);
      setOpen(true);
      return;
    }

    const response = await process({ id: order.id, statusId: action.statusId });
    if (!response) return;

    orders.set(response.id, new Order(response));
    setOrders(new Map(orders));

    actions.onBack();
  };

  const actionButtons = [];
  for (const nextAction of potentialNextActions) {
    if (nextAction.trigger.auto) continue;

    actionButtons.push(
      <Button
        key={nextAction.id}
        onClick={() => handleAction(nextAction)}
        text={orderStatuses.get(nextAction.statusId)?.name}
      />,
    );
  }

  const backButton = (
    <Button
      variant={ButtonVariant.secondary}
      onClick={actions.onBack}
      text={t(TranslationKey.back).singular.label}
      disabled={loading.update || processLoading}
    />
  );

  return (
    <div>
      <GXOCustomFieldModal open={open} setOpen={setOpen} order={order} setOrder={setOrder} actionId={actionId} />
      <div className='flex justify-between'>
        <div className='flex gap-2'>{[backButton]}</div>
        <div className='flex gap-2'>{actionButtons}</div>
      </div>
    </div>
  );
}
